import { IconButton } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { Skeleton } from "@dropbox/dig-components/skeleton";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AiChatLine,
  EditLine,
  HelpLine,
  MoreHorizontalLine,
} from "@dropbox/dig-icons/assets";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { useAtom } from "jotai";
import { memo, useCallback, useState } from "react";
import styles from "./ConversationListRows.module.css";

import { useTheme } from "@dropbox/dig-foundations";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashChatAction } from "@mirage/analytics/events/types/click_dash_chat_action";
import { parseMarkdownContent } from "@mirage/mosaics/ComposeAssistant/data/markdown-utils";
import { conversationPageLoadingAtom } from "@mirage/mosaics/ConversationAssistant/hooks/useConversationPageLoading";
import type { ConversationListState } from "@mirage/service-conversation/hooks/useConversationsList";
import type { ConversationMetadata } from "@mirage/service-conversation/types";
import { tagged } from "@mirage/service-logging";
import type { ComposeAssistantConversationMessage } from "@mirage/shared/compose/assist-message";
import {
  getFirstMarkdownArtifact,
  type ComposeSession,
} from "@mirage/shared/compose/compose-session";
import { onKeyDownCommitFn } from "@mirage/shared/util/on-key-down";

const logger = tagged("ConversationListRows");

interface ConversationListProps {
  conversationListState: ConversationListState;
  conversationsToDisplay: (ConversationMetadata | ComposeSession)[];
  currentConversationServerId: string | undefined;
  onClickConversation: (conversation: ConversationMetadata) => void;
  setShowModal: (show: boolean) => void;
  onDeleteConversation?: (conversation: ConversationMetadata) => void;
  handleLegacySessionClick: (session: ComposeSession) => void;
}

export const ConversationListRows = memo(
  ({
    conversationListState,
    conversationsToDisplay,
    currentConversationServerId,
    onClickConversation,
    setShowModal,
    onDeleteConversation,
    handleLegacySessionClick,
  }: ConversationListProps) => {
    const loadingBody = (
      <div className={styles.ConversationListLoadingState}>
        <Skeleton.Text size="medium" withBorderRadius="xsmall" width={100} />
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className={styles.ConversationListSkeletonRow}>
            <Skeleton.Box height={16} width={16} withBorderRadius="xsmall" />
            <Skeleton.Text size="medium" withBorderRadius="xsmall" />
          </div>
        ))}
      </div>
    );

    return (
      <div className={styles.ConversationListContainer}>
        <div className={styles.ConversationListContent}>
          <div className={styles.ConversationRows}>
            {conversationListState.type === "loading" ? (
              loadingBody
            ) : (
              <div className={styles.ConversationRowsGroup}>
                {conversationsToDisplay.length > 0 && (
                  <Text
                    variant="label"
                    className={styles.ConversationRowsGroupTitle}
                    isBold
                    size="small"
                    tagName="h2"
                    color="subtle"
                  >
                    {i18n.t("recent_sessions")}
                  </Text>
                )}
                {conversationsToDisplay.map((item) => {
                  // Check if new session (has serverConversationId) or a legacy session
                  if ("serverConversationId" in item) {
                    return (
                      <ConversationRow
                        key={item.serverConversationId}
                        conversation={item}
                        isSelected={
                          currentConversationServerId ===
                          item.serverConversationId
                        }
                        onClickConversation={onClickConversation}
                        onDeleteConversation={onDeleteConversation}
                      />
                    );
                  } else {
                    return (
                      <LegacySessionRow
                        key={item.id}
                        session={item}
                        onClickSession={handleLegacySessionClick}
                      />
                    );
                  }
                })}
                {conversationsToDisplay.length > 0 && (
                  <div
                    className={styles.ConversationRow}
                    onClick={() => setShowModal(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={onKeyDownCommitFn(() => setShowModal(true))}
                  >
                    <div className={styles.ConversationRowHeader}>
                      <div className={styles.ConversationRowIcon}>
                        <UIIcon src={MoreHorizontalLine} size="small" />
                      </div>
                      <Text
                        size="medium"
                        tagName="div"
                        className={styles.ConversationRowTitle}
                        color="inherit"
                      >
                        {i18n.t("show_more")}
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);
ConversationListRows.displayName = "ConversationListRows";

interface ConversationRowProps {
  conversation: ConversationMetadata;
  isSelected: boolean;
  onClickConversation: (conversation: ConversationMetadata) => void;
  onDeleteConversation?: (conversation: ConversationMetadata) => void;
  largeDisplay?: boolean;
  largeIcon?: boolean;
}

export const ConversationRow = memo(
  ({
    conversation,
    isSelected,
    onClickConversation,
    onDeleteConversation,
    largeDisplay = false,
    largeIcon,
  }: ConversationRowProps) => {
    const { reportPapEvent } = useMirageAnalyticsContext();
    const isDark = useTheme();
    const [showActions, setShowActions] = useState(false);
    const [, setIsConversationPageLoading] = useAtom(
      conversationPageLoadingAtom,
    );
    const handleClick = () => {
      setIsConversationPageLoading(true);
      onClickConversation(conversation);
    };
    const handleClickDelete = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        reportPapEvent(
          PAP_Click_DashChatAction({
            featureLine: "assist",
            dashChatAction: "delete_previous_session",
          }),
        );
        if (onDeleteConversation) {
          onDeleteConversation(conversation);
        } else {
          logger.error("Delete conversation handler not provided");
        }
      },
      [conversation, onDeleteConversation],
    );
    return (
      <div
        className={classNames(styles.ConversationRow, {
          [styles.ConversationRowSelected]: isSelected,
          [styles.ConversationRowDark]: isDark,
        })}
        onClick={handleClick}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
        role="button"
        tabIndex={0}
        onKeyDown={onKeyDownCommitFn(handleClick)}
      >
        <div className={styles.ConversationRowHeader}>
          <div
            className={classNames(styles.ConversationRowIcon, {
              [styles.ConversationRowIconLarge]: largeDisplay,
            })}
          >
            <UIIcon
              className={classNames({
                [styles.ConversationRowIconLargeFont]: largeIcon,
              })}
              src={getIconForAgent(conversation)}
              size={largeDisplay ? "medium" : "small"}
            />
          </div>
          {largeDisplay ? (
            // Large display mode for modal view
            <div className={styles.ConversationContent}>
              <Text
                size="medium"
                tagName="div"
                className={styles.ConversationTitle}
                color="standard"
              >
                {conversation.title}
              </Text>
              <Text
                size="small"
                tagName="div"
                className={styles.ConversationPreview}
                color="subtle"
              >
                {conversation.preview}
              </Text>
            </div>
          ) : (
            <Text
              size="medium"
              tagName="div"
              className={styles.ConversationRowTitle}
              color="standard"
            >
              {conversation.title}
            </Text>
          )}
          {showActions && (
            <ConversationRowActionsMenu onClickDelete={handleClickDelete} />
          )}
        </div>
      </div>
    );
  },
);
ConversationRow.displayName = "ConversationRow";

function getIconForAgent(conversation: ConversationMetadata) {
  switch (conversation.initialAgent) {
    case "write":
      return EditLine;
    case "summarize":
      return HelpLine;
    default:
      return AiChatLine;
  }
}

interface ConversationRowActionsMenuProps {
  onClickDelete: (e: React.MouseEvent) => void;
}
export const ConversationRowActionsMenu = memo(
  ({ onClickDelete }: ConversationRowActionsMenuProps) => {
    return (
      <Menu.Wrapper
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {({ getContentProps, getTriggerProps }) => {
          return (
            <>
              <div className={styles.ConversationRowMenuWrapper}>
                <IconButton
                  {...getTriggerProps()}
                  variant="borderless"
                  size="small"
                >
                  <UIIcon src={MoreHorizontalLine} />
                </IconButton>
              </div>
              <Menu.Content {...getContentProps()}>
                <Menu.Segment>
                  <Menu.ActionItem
                    key="delete"
                    value="Delete"
                    onClick={onClickDelete}
                  >
                    {i18n.t("delete")}
                  </Menu.ActionItem>
                </Menu.Segment>
              </Menu.Content>
            </>
          );
        }}
      </Menu.Wrapper>
    );
  },
);
ConversationRowActionsMenu.displayName = "ConversationRowActionsMenu";

interface LegacySessionRowProps {
  session: ComposeSession;
  onClickSession: (session: ComposeSession, onClose?: () => void) => void;
  largeDisplay?: boolean;
  onClose?: () => void;
}
export const LegacySessionRow = memo(
  ({
    session,
    onClickSession,
    largeDisplay = false,
    onClose,
  }: LegacySessionRowProps) => {
    const isDark = useTheme();
    // Use the same logic to derive title as in SessionsList
    const markdownArtifact = getFirstMarkdownArtifact(session.artifacts);
    const firstUserMessage = session.messagesHistory.find(
      (message) => message.type === "message" && message.role === "user",
    ) as ComposeAssistantConversationMessage | undefined;

    let { title } = parseMarkdownContent(
      markdownArtifact?.markdownContent || "",
    );
    title =
      title ||
      (firstUserMessage?.type === "message"
        ? firstUserMessage.text
        : undefined) ||
      i18n.t("compose_session_untitled_label");

    return (
      <div
        className={classNames(styles.ConversationRow, {
          [styles.ConversationRowDark]: isDark,
        })}
        onClick={() => onClickSession(session, onClose)}
        role="button"
        tabIndex={0}
        onKeyDown={onKeyDownCommitFn(() => onClickSession(session, onClose))}
      >
        <div className={styles.ConversationRowHeader}>
          <div
            className={classNames(styles.ConversationRowIcon, {
              [styles.ConversationRowIconLarge]: largeDisplay,
            })}
          >
            <UIIcon
              src={getIconForLegacySession(session)}
              size={largeDisplay ? "medium" : "small"}
            />
          </div>
          {largeDisplay ? (
            // Large display mode for modal view
            <div className={styles.ConversationContent}>
              <Text
                size="medium"
                tagName="div"
                className={styles.ConversationTitle}
                color="standard"
              >
                {title}
              </Text>
            </div>
          ) : (
            <Text
              size="medium"
              tagName="div"
              className={styles.ConversationRowTitle}
              color="standard"
            >
              {title}
            </Text>
          )}
        </div>
      </div>
    );
  },
);
LegacySessionRow.displayName = "LegacySessionRow";

function getIconForLegacySession(session: ComposeSession) {
  const hasMarkdownArtifact = session.artifacts.some(
    (artifact) => artifact.type === "markdown_draft",
  );
  return hasMarkdownArtifact ? EditLine : AiChatLine;
}
